import { useState } from "react";

import { Card } from "components/ui/card";
import { Switch } from "components/ui/switch";

import type { PublicEvent } from "../events/schemas";
import { useNow } from "../event-experience/use-now";
import { afterEventVisible } from "./after-event";
import { AfterEventSection } from "./AfterEventSection";

/**
 * The top of the Overview: the "After the event" section once it shows, and
 * for an event admin until then, a switch to preview it (TNG-96).
 *
 * The preview is local to this page and changes nothing for anybody else.
 * It shows the section as an attendee will see it after the end, whether or
 * not the section is switched on yet, so the organiser can check the
 * summary, the resources and the PDF before the event ends.
 */
export function AfterEventOverview({
  event,
  uid,
  isEventAdmin,
  seeAllResources,
  hasSignals,
}: {
  event: PublicEvent;
  uid: string;
  isEventAdmin: boolean;
  seeAllResources: boolean;
  hasSignals: boolean;
}) {
  const now = useNow(60_000);
  const [preview, setPreview] = useState(false);
  const visible = afterEventVisible(event, now);
  const show = visible || (isEventAdmin && preview);

  return (
    <>
      {isEventAdmin && !visible && (
        <Card data-testid="after-event-preview">
          {/* The whole row is the label, so the target is the row: at least
              44px tall on a phone, and a tap on the words flips the switch. */}
          <label
            htmlFor="after-event-preview-switch"
            className="flex min-h-11 cursor-pointer items-center justify-between gap-4 px-6 py-3"
          >
            <span>
              <span className="block text-sm font-medium text-foreground">
                Preview as attendee after the event
              </span>
              <span className="block text-xs text-muted-foreground">
                {event.configAfterEvent === true
                  ? "Attendees see this section once the event has ended."
                  : "Attendees do not see this section: it is switched off in Settings."}{" "}
                Only you see the preview.
              </span>
            </span>
            <Switch
              id="after-event-preview-switch"
              checked={preview}
              onCheckedChange={setPreview}
              data-testid="toggle-after-event-preview"
            />
          </label>
        </Card>
      )}
      {show && (
        <AfterEventSection
          eventId={event.eventId}
          eventTitle={event.title}
          timeZone={event.timezone}
          uid={uid}
          seeAllResources={seeAllResources}
          hasSignals={hasSignals}
          connectionsEnabled={event.configConnections === true}
        />
      )}
    </>
  );
}
